import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Image,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import CoverUploadModal from "../../../shared/components/CoverUploadModal";
import ReadingOrderAutocomplete from "./ReadingOrderAutocomplete";

const emptyForm = {
  title: "",
  description: "",
  startsAt: "",
  endsAt: "",
  published: false,
  coverFile: null,
  coverPreview: null,
  readingOrderId: "",
  readingOrderOption: null,
  threads: [],
  removedThreadIds: [],
};

const formFromClub = (club) => {
  if (!club) {
    return emptyForm;
  }

  const readingOrder =
    club.readingOrder || club.readingOrders?.[0]?.readingOrder || null;

  return {
    title: club.title || "",
    description: club.description || "",
    startsAt: club.startsAt || "",
    endsAt: club.endsAt || "",
    published: Boolean(club.published),
    coverFile: null,
    coverPreview: club.cover || null,
    readingOrderId: readingOrder?.id ? String(readingOrder.id) : "",
    readingOrderOption: readingOrder,
    threads: (club.threads || [])
      .filter((thread) => !thread.hiddenAt)
      .map((thread) => ({
        id: thread.id,
        title: thread.title || "",
        body: thread.body || "",
        spoilerOrderPosition: thread.spoilerOrderPosition || "",
      })),
    removedThreadIds: [],
  };
};

const BookClubAdminForm = ({ club, onSubmit, isSaving }) => {
  const { isOpen, onToggle } = useDisclosure();
  const [form, setForm] = useState(() => formFromClub(club));
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    setForm(formFromClub(club));
    setValidationError("");
  }, [club]);

  const updateForm = (field, value) => {
    setValidationError("");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateReadingOrder = (readingOrder) => {
    setValidationError("");
    setForm((current) => ({
      ...current,
      readingOrderId: String(readingOrder.id),
      readingOrderOption: readingOrder,
    }));
  };

  const addQuestion = () =>
    setForm((current) => ({
      ...current,
      threads: [
        ...current.threads,
        { title: "", body: "", spoilerOrderPosition: "" },
      ],
    }));

  const updateQuestion = (index, field, value) => {
    setValidationError("");
    setForm((current) => ({
      ...current,
      threads: current.threads.map((thread, threadIndex) =>
        threadIndex === index ? { ...thread, [field]: value } : thread
      ),
    }));
  };

  const removeQuestion = (index) =>
    setForm((current) => {
      const thread = current.threads[index];
      return {
        ...current,
        threads: current.threads.filter(
          (_, threadIndex) => threadIndex !== index
        ),
        removedThreadIds: thread.id
          ? [...current.removedThreadIds, thread.id]
          : current.removedThreadIds,
      };
    });

  const onCropSubmitted = (file, url) => {
    setForm((current) => ({
      ...current,
      coverFile: file,
      coverPreview: url,
    }));
    onToggle();
  };

  const submit = (event) => {
    event.preventDefault();
    const readingOrderId = String(form.readingOrderId || "");
    const hasIncompleteQuestion = form.threads.some(
      (thread) => !thread.title.trim() || !thread.body.trim()
    );

    if (!readingOrderId) {
      setValidationError("Choose a published reading order.");
      return;
    }
    if (hasIncompleteQuestion) {
      setValidationError("Complete or remove every leading question.");
      return;
    }

    onSubmit({ ...form, readingOrderId });
  };

  return (
    <Box as="form" onSubmit={submit}>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="6">
        <Box bg="gray.700" borderRadius="lg" p={{ base: "5", md: "6" }}>
          <Heading size="md">Club details</Heading>
          <Stack mt="6" spacing="5">
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input
                value={form.title}
                onChange={(event) => updateForm("title", event.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={form.description}
                onChange={(event) =>
                  updateForm("description", event.target.value)
                }
                rows={6}
              />
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="5">
              <FormControl isRequired>
                <FormLabel>Starts</FormLabel>
                <Input
                  type="date"
                  value={form.startsAt}
                  onChange={(event) =>
                    updateForm("startsAt", event.target.value)
                  }
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Ends</FormLabel>
                <Input
                  type="date"
                  value={form.endsAt}
                  onChange={(event) => updateForm("endsAt", event.target.value)}
                />
              </FormControl>
            </SimpleGrid>
            <FormControl isRequired>
              <FormLabel>Publication state</FormLabel>
              <Select
                aria-label="Publication state"
                value={form.published ? "published" : "draft"}
                onChange={(event) =>
                  updateForm("published", event.target.value === "published")
                }
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </Select>
              <Text mt="2" color="gray.400" fontSize="sm">
                Draft clubs stay hidden until you publish them.
              </Text>
            </FormControl>

            <Box>
              <FormLabel>Cover image</FormLabel>
              <Box
                bg="gray.800"
                borderRadius="md"
                overflow="hidden"
                maxW="500px"
              >
                {form.coverPreview ? (
                  <Image
                    src={form.coverPreview}
                    alt="Book club cover preview"
                    w="full"
                    aspectRatio={1000 / 640}
                    objectFit="cover"
                  />
                ) : (
                  <Center minH="220px" px="6" textAlign="center">
                    <Text color="gray.400">No cover image selected yet.</Text>
                  </Center>
                )}
              </Box>
              <Button mt="3" onClick={onToggle} variant="outline">
                {form.coverPreview
                  ? "Replace cover image"
                  : "Upload cover image"}
              </Button>
              <CoverUploadModal
                onToggle={onToggle}
                isOpen={isOpen}
                onSubmit={onCropSubmitted}
                title="Upload a book club cover image"
                aspectRatio={1000 / 640}
                croppedCanvasConfig={{
                  width: 1000,
                  height: 640,
                  minWidth: 1000,
                  minHeight: 640,
                  imageSmoothingEnabled: true,
                  imageSmoothingQuality: "high",
                }}
              />
            </Box>
          </Stack>
        </Box>

        <Box bg="gray.700" borderRadius="lg" p={{ base: "5", md: "6" }}>
          <Heading size="md">Reading order</Heading>
          <Text mt="2" color="gray.400" fontSize="sm">
            Members will automatically add this reading order when they join the
            club.
          </Text>
          <Box mt="5">
            <ReadingOrderAutocomplete
              ariaLabel="Reading order"
              value={form.readingOrderOption}
              onSelectionChange={updateReadingOrder}
            />
          </Box>

          <Heading size="md" mt="10">
            Leading questions
          </Heading>
          <Text mt="2" color="gray.400" fontSize="sm">
            Add the spoiler-aware discussion prompts that should open with the
            club.
          </Text>
          <Stack mt="5" spacing="5">
            {form.threads.map((thread, index) => (
              <Box
                key={thread.id || `new-${index}`}
                borderWidth="1px"
                borderColor="gray.600"
                borderRadius="md"
                p="4"
              >
                <Flex justify="space-between" align="center" gap="3">
                  <Text fontWeight="bold">Question {index + 1}</Text>
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    onClick={() => removeQuestion(index)}
                  >
                    Remove
                  </Button>
                </Flex>
                <FormControl isRequired mt="4">
                  <FormLabel>Question</FormLabel>
                  <Input
                    value={thread.title}
                    onChange={(event) =>
                      updateQuestion(index, "title", event.target.value)
                    }
                    placeholder="What should members notice in this story?"
                  />
                </FormControl>
                <FormControl isRequired mt="4">
                  <FormLabel>Prompt / context</FormLabel>
                  <Textarea
                    value={thread.body}
                    onChange={(event) =>
                      updateQuestion(index, "body", event.target.value)
                    }
                    rows={4}
                  />
                </FormControl>
                <FormControl mt="4">
                  <FormLabel>Unlock after reading order</FormLabel>
                  <Select
                    value={thread.spoilerOrderPosition}
                    onChange={(event) =>
                      updateQuestion(
                        index,
                        "spoilerOrderPosition",
                        event.target.value
                      )
                    }
                  >
                    <option value="">Available immediately</option>
                    {form.readingOrderId ? (
                      <option value="1">
                        1. {form.readingOrderOption?.title || "Reading order"}
                      </option>
                    ) : null}
                  </Select>
                </FormControl>
              </Box>
            ))}
          </Stack>
          <Button type="button" mt="4" size="sm" onClick={addQuestion}>
            Add leading question
          </Button>

          {validationError ? (
            <FormControl isInvalid mt="5">
              <FormErrorMessage>{validationError}</FormErrorMessage>
            </FormControl>
          ) : null}
          <Button mt="6" colorScheme="red" type="submit" isLoading={isSaving}>
            {form.published ? "Publish book club" : "Save draft"}
          </Button>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default BookClubAdminForm;
